import { HttpClient } from "@angular/common/http";
import { EventEmitter, Inject, Injectable, Output } from "@angular/core";
import { CookieService } from "ngx-cookie-service";
import { Observable, lastValueFrom, tap } from "rxjs";
import { ChangePassword } from "../models/change-password.model";
import { LoginRequest } from "../models/login-request.model";
import { LoginResult } from "../models/login-result.model";
import { ResetPassword } from "../models/reset-password.model";
import { User } from "../models/user.model";
import { VupConsoleAccount } from "../models/vup-console-account.model";

@Injectable({
    providedIn: 'root'
})
export class AccountService {
    private loggedUser: User | undefined;
    private _selectedAccount: VupConsoleAccount | null = null;

    public get USER_SESSION(): string { return "session"; };

    @Output() userLoggedIn: EventEmitter<User> = new EventEmitter<User>();
    @Output() userLoggedRefresh: EventEmitter<User> = new EventEmitter<User>();
    @Output() userLoggedOut: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() loggedUserUpdated: EventEmitter<User> = new EventEmitter<User>();
    @Output() accountSelected: EventEmitter<void> = new EventEmitter<void>();

    constructor(
        @Inject('API_GATEWAY_URL') private readonly apiUrl: string,
        @Inject('DONE_APP_DOMAIN') private readonly doneAppDomain: string,
        private readonly _http: HttpClient,
        private _cookieService: CookieService
    ) { }

    //#region Requests
    login(req: LoginRequest): Observable<LoginResult> {
        return this._http.post<LoginResult>(`${this.apiUrl}/auth/users/login`, req).pipe(
            tap({
                next: async (result: LoginResult) => {
                    this._setSessionData(result);

                    await this.me();
                    this.userLoggedIn.emit(this.loggedUser);
                }
            })
        );
    }

    refreshToken(): Observable<LoginResult> {
        const session = this.getSessionData();

        if (!session)
            throw new Error("Nenhuma sessão ativa encontrada.");

        const req = {
            accessToken: session?.token,
            refreshToken: session?.refreshToken
        };

        return this._http.post<LoginResult>(`${this.apiUrl}/auth/users/refresh-token`, req).pipe(
            tap({
                next: async (result: LoginResult) => {
                    // AT-1224: o backend não sabe (e nunca vai saber) qual conta estava
                    // selecionada — "conta selecionada" é um conceito só de frontend
                    // (selectAccount/appendToSessionData), o LoginResultVm do refresh
                    // nem tem essa propriedade. Mesclar só os campos de autenticação
                    // (em vez de substituir a sessão inteira por `result`) preserva
                    // `account` e qualquer outro campo já anexado à sessão — sem isso,
                    // toda renovação apagava a conta selecionada silenciosamente.
                    const currentSession = this.getSessionData();

                    this._setSessionData({
                        ...currentSession,
                        id: result.id,
                        token: result.token,
                        tokenValidity: result.tokenValidity,
                        refreshToken: result.refreshToken,
                        roles: result.roles,
                    });

                    await this.me();
                    this.userLoggedRefresh.emit(this.loggedUser);
                }
            })
        );
    }

    logout(logoutManual: boolean = false): Observable<any> {
        return this._http.patch(`${this.apiUrl}/auth/users/logout`, {}).pipe(
            tap({
                next: _ => this.clearSessionInfo(logoutManual)
            })
        );
    }

    changePassword(model: ChangePassword): Observable<boolean> {
        return this._http.patch<boolean>(`${this.apiUrl}/auth/users/changePassword`, model)
    }

    requestPasswordRecovery(email: string): Observable<boolean> {
        return this._http.patch<boolean>(`${this.apiUrl}/auth/users/requestPasswordRecovery?email=${email}`, {})
    }

    recoverPassword(model: ResetPassword): Observable<boolean> {
        return this._http.patch<boolean>(`${this.apiUrl}/auth/users/recoverPassword`, model)
    }

    async me(): Promise<User | undefined> {
        try {
            if (!!this.loggedUser) return this.loggedUser;

            this.loggedUser = await lastValueFrom(this._http.get<User>(`${this.apiUrl}/auth/users/me`));
            return this.loggedUser;
        } catch (e: any) {
            return undefined;
        }
    }
    //#endregion

    isLoggedIn(): boolean {
        const sessionData: LoginResult | null = this.getSessionData();

        if (!sessionData) return false;

        if (new Date(sessionData.tokenValidity) < new Date()) return false;

        return true;
    }

    getToken(): string | null {
        const sessionData: LoginResult | null = this.getSessionData();

        return sessionData?.token ?? null;
    }

    getUserId(): string | null {
        const sessionData: LoginResult | null = this.getSessionData();

        return sessionData?.id ?? null;
    }

    selectAccount(account: VupConsoleAccount | null): void {
        this._selectedAccount = account;

        const session = this.getSessionData();

        if (!!session) {
            session.account = account;

            this._setSessionData(session);
        }

        this.accountSelected.emit();
    }

    public get selectedAccount(): VupConsoleAccount | null {
        this._selectedAccount ??= this.getSessionData()?.account ?? null;

        return this._selectedAccount;
    }

    public get isValorUp(): boolean {
        const account = this.selectedAccount;

        if (!account)
            return false;

        return account.domain === "valorup.com.br";
    };

    private _setSessionData(result: LoginResult) {
        if (this._cookieService.check(this.USER_SESSION))
            this._cookieService.delete(this.USER_SESSION);

        this._cookieService.set(this.USER_SESSION, JSON.stringify(result), {
            path: "/",
            domain: this.doneAppDomain
        });
    }

    clearSessionInfo(logoutManual: boolean = false) {
        this._selectedAccount = null;

        if (this._cookieService.check(this.USER_SESSION))
            this._cookieService.delete(this.USER_SESSION);

        this.loggedUser = undefined;

        this.userLoggedOut.emit(logoutManual);
    }

    getSessionData(): LoginResult | any | null {
        const json = this._cookieService.get(this.USER_SESSION);

        if (!json) return null;

        return JSON.parse(json);
    }

    appendToSessionData(key: string, value: any): void {
        const data = this.getSessionData();

        if (!data)
            return;

        data[key] = value;

        this._setSessionData(data);
    }

    updateLoggedUser(user: User): void {
        this.loggedUser = user;

        this.loggedUserUpdated.emit(user);
    }
}